import {
  lazy,
  Suspense,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCatalogo, useEventosCatalogo, usePerfil } from "./api/queries";
import { useEditor } from "./state/editor";
import { nuevaCotizacion } from "./domain/model";
import { firmaDePerfil } from "./domain/perfil";
import { Login, leerVendedor, SESSION_KEY } from "./components/Login";
import Cliente from "./components/Cliente";
import Resumen from "./components/Resumen";
import Propuestas from "./components/Propuestas";
import AccionesCotizacion from "./components/AccionesCotizacion";
import OrdenProduccion from "./components/OrdenProduccion";
import { ErrorAviso } from "./components/ui";
import Icono from "./components/Icono";
import { useConfirmar } from "./components/Dialogos";
import { useAvisos } from "./components/Avisos";
import {
  avisarVencida,
  borrarSesion,
  EVENTO_VENCIDA,
  leerSesion,
  vencimientoDe,
} from "./api/sesion";
import { erroresDelPaso2, validarCotizacion } from "./domain/pricing";
import { explicarError } from "./domain/errores";
import { useGuardarCotizacion } from "./state/useGuardarCotizacion";
import {
  leerBorrador,
  restaurarBorrador,
  useAutoguardado,
} from "./state/borrador";
import MenuCuenta from "./components/MenuCuenta";
import type { SeccionCuenta } from "./components/MiCuenta";
const Paso2 = lazy(() => import("./components/paso2/Paso2"));
const Margen = lazy(() => import("./components/Margen"));
const Historial = lazy(() => import("./components/Historial"));
const Admin = lazy(() => import("./components/Admin"));
const Sugerencia = lazy(() => import("./components/Sugerencia"));
const MiCuenta = lazy(() => import("./components/MiCuenta"));
export default function App() {
  const [sesion, setVendedor] = useState(leerVendedor),
    [paso, setPaso] = useState(1),
    // Celular: las acciones de la barra van en un menú desplegable.
    [menuMovil, setMenuMovil] = useState(false),
    irAPieza = useEditor((s) => s.irAPieza),
    [guardando, setBusy] = useState(false),
    [modal, setModal] = useState<string | null>(null),
    // `/admin.html` es el marcador viejo de la época vanilla; el fallback SPA
    // lo sirve redirigiendo a `/admin`, así que se aceptan las dos formas.
    // Abrir esa ruta solo pide el panel: quien puede verlo lo decide el rol
    // más abajo (y el backend lo vuelve a exigir en cada endpoint).
    [admin, setAdmin] = useState(() =>
      /^\/admin(\.html)?\/?$/.test(location.pathname),
    ),
    [intentosPaso3, setIntentosPaso3] = useState(0);
  const cabecera = useRef<HTMLElement>(null);
  // Página "Mi cuenta" abierta en esa sección, o null (cotizador / admin).
  const [cuenta, setCuenta] = useState<SeccionCuenta | null>(null);
  const vendedorActual = useRef(sesion),
    duenoBorrador = useRef<string | null>(null);
  vendedorActual.current = sesion;
  const tareasActivas = useEditor((s) => s.tareasActivas),
    busy = guardando || tareasActivas > 0;
  const [tema, setTema] = useState<"claro" | "oscuro">(() => {
    try {
      return localStorage.getItem("cotizador_tema") === "claro"
        ? "claro"
        : "oscuro";
    } catch {
      return "oscuro";
    }
  });
  const dirty = useEditor((s) => s.dirty),
    numero = useEditor((s) => s.cotizacion.numero),
    numeroOficial = useEditor((s) => s.cotizacion.numeroOficial),
    catalogo = useCatalogo(),
    confirmar = useConfirmar(),
    avisos = useAvisos(),
    guardarCotizacion = useGuardarCotizacion(),
    variasPropuestas = useEditor((s) => s.propuestas.length > 1),
    client = useQueryClient();
  useEventosCatalogo();
  // n8n cobra con el precio vigente de Airtable: al abrir una cotización (o
  // cuando cambia el catálogo) la vista previa se alinea con ese precio.
  const cargas = useEditor((s) => s.cargas);
  useEffect(() => {
    if (catalogo.data)
      useEditor.getState().aplicarPreciosVigentes(catalogo.data);
  }, [catalogo.data, cargas]);
  // La firma del PDF se lee del perfil en cada carga (no de la sesión
  // guardada), así un cambio en "Mi perfil" se refleja sin volver a entrar.
  const perfil = usePerfil(sesion?.id);
  const vendedor = useMemo(
    () =>
      sesion && perfil.data
        ? {
            ...sesion,
            firma: firmaDePerfil(perfil.data),
            // undefined (backend anterior) no bloquea; null = sin asignar.
            whatsapp: perfil.data.whatsapp,
          }
        : sesion,
    [sesion, perfil.data],
  );
  // Borrador sin guardar: se copia al navegador mientras se edita y, al
  // entrar (o volver a cargar el cotizador dentro del CRM), se ofrece
  // recuperarlo. Una vez por vendedor y por carga de la página.
  useAutoguardado(vendedor?.id);
  const borradorOfrecido = useRef<string | null>(null);
  useEffect(() => {
    const id = vendedor?.id;
    if (!id || borradorOfrecido.current === id) return;
    borradorOfrecido.current = id;
    const b = leerBorrador(id);
    // Si ya hay trabajo en pantalla (p. ej. volvió a entrar tras vencer la
    // sesión), ese manda: el autoguardado lo reemplaza enseguida.
    if (!b || useEditor.getState().dirty) return;
    const cliente = b.cotizacion.cliente.nombre.trim() || "sin cliente";
    const proyecto = b.cotizacion.cliente.nombre_proyecto?.trim();
    const cuando = new Date(b.guardadoEn).toLocaleString("es-EC", {
      dateStyle: "short",
      timeStyle: "short",
    });
    // Tres salidas: Recuperar, Descartar (borra ese borrador) o cerrar el
    // diálogo (Escape, la X): no decide nada y se ofrece en la próxima carga.
    void confirmar({
      titulo: "Recuperar la cotización sin guardar",
      mensaje: `Quedó una cotización sin guardar del ${cuando}: ${cliente}${proyecto ? ` · ${proyecto}` : ""}. ¿La recuperas?`,
      confirmar: "Recuperar",
      cancelar: "Ahora no",
    }).then((ok) => {
      if (!ok) return;
      restaurarBorrador(b, id);
      startTransition(() => setPaso(1));
      if (b.cotizacion.registroId)
        avisos.mostrar({
          tipo: "info",
          titulo: "Cotización recuperada",
          mensaje:
            "Ya estaba guardada antes. Si al guardar dice que cambió, ábrela desde Mis cotizaciones y vuelve a aplicar los cambios.",
        });
    });
  }, [vendedor?.id, confirmar]);
  // Sesión vencida o rechazada (api/sesion.ts): vuelve al login con un aviso.
  // La cotización en pantalla NO se descarta: el editor vive fuera de este
  // componente y sigue ahí al reingresar con el PIN.
  useEffect(() => {
    const vencida = (e: Event) => {
      const porPin = (e as CustomEvent).detail === "pin";
      try {
        localStorage.removeItem(SESSION_KEY);
      } catch {
        // nada que borrar
      }
      // De quién es lo que queda en pantalla: solo ese vendedor lo recupera
      // al volver a entrar (ver onLogin).
      if (vendedorActual.current)
        duenoBorrador.current = vendedorActual.current.id;
      setVendedor(null);
      setCuenta(null);
      avisos.mostrar(
        porPin
          ? {
              tipo: "exito",
              titulo: "PIN actualizado",
              mensaje:
                "Entra con tu PIN nuevo. Lo que no guardaste sigue en pantalla.",
            }
          : {
              tipo: "info",
              titulo: "Tu sesión venció",
              mensaje:
                "Vuelve a ingresar con tu PIN. Lo que no guardaste sigue en pantalla.",
            },
      );
    };
    window.addEventListener(EVENTO_VENCIDA, vencida);
    return () => window.removeEventListener(EVENTO_VENCIDA, vencida);
  }, [avisos]);
  // Y al vencer, aunque no se haga ninguna llamada (catálogo ya en memoria):
  // si no, se enteraría recién al guardar.
  useEffect(() => {
    if (!vendedor) return;
    const token = leerSesion();
    const exp = token ? vencimientoDe(token) : null;
    if (!exp) return;
    // setTimeout no admite más de ~24,8 días; las sesiones duran horas.
    const t = setTimeout(
      avisarVencida,
      Math.min(exp - Date.now(), 2 ** 31 - 1),
    );
    return () => clearTimeout(t);
  }, [vendedor]);
  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    localStorage.setItem("cotizador_tema", tema);
  }, [tema]);
  // La barra superior es sticky; el panel de resumen también, y tiene que
  // quedar por debajo de ella. El alto real cambia (los botones envuelven en
  // pantallas angostas), así que se mide en vez de fijarlo a ojo.
  useEffect(() => {
    const header = cabecera.current;
    if (!header || typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(() =>
      document.documentElement.style.setProperty(
        "--alto-header",
        `${header.offsetHeight}px`,
      ),
    );
    observador.observe(header);
    return () => observador.disconnect();
  }, [vendedor]);
  useEffect(() => {
    if (!dirty && !busy) return;
    const fn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", fn);
    return () => window.removeEventListener("beforeunload", fn);
  }, [dirty, busy]);
  useEffect(() => {
    if (new URLSearchParams(location.search).has("recarga"))
      void client.invalidateQueries({ queryKey: ["saldo"] });
  }, [client]);
  async function reemplazar() {
    return (
      !dirty ||
      (await confirmar({
        titulo: "Hay cambios sin guardar",
        mensaje: "Si continúas se descartan los cambios de esta cotización.",
        confirmar: "Descartar cambios",
        peligro: true,
      }))
    );
  }
  // No se pasa al paso 3 con piezas incompletas: antes el error recién salía
  // al generar el PDF. Valida todas las propuestas, pero solo lo del paso 2:
  // formato, margen, descuento e IVA se corrigen justamente en el paso 3.
  function erroresAntesDelPaso3() {
    const todas = useEditor.getState().todas();
    return [
      ...new Set(
        todas.flatMap((c) =>
          erroresDelPaso2(c).map((e) =>
            todas.length > 1 && c.etiquetaPropuesta
              ? `${c.etiquetaPropuesta}: ${e}`
              : e,
          ),
        ),
      ),
    ];
  }
  // "Guardar" de la barra: el mismo guardado que el del paso 3 (ver
  // useGuardarCotizacion), desde cualquier paso, para que Mis cotizaciones
  // quede al día sin tener que llegar al final.
  async function guardarDesdeBarra() {
    if (!vendedor || busy) return;
    if (variasPropuestas) {
      startTransition(() => setPaso(3));
      avisos.mostrar({
        tipo: "info",
        titulo: "Hay varias propuestas",
        mensaje: "Guárdalas con «Guardar propuestas» al final del paso 3.",
      });
      return;
    }
    if (!dirty && numeroOficial) {
      avisos.mostrar({
        tipo: "info",
        titulo: "Sin cambios por guardar",
        mensaje: `${numeroOficial} ya está al día en Mis cotizaciones.`,
      });
      return;
    }
    const errores = validarCotizacion(useEditor.getState().cotizacion);
    if (errores.length) {
      useEditor.getState().pedirRevision();
      avisos.mostrar({
        tipo: "error",
        titulo: "Antes de guardar, corrige esto",
        mensaje:
          errores.slice(0, 5).join(" · ") +
          (errores.length > 5 ? ` · y ${errores.length - 5} más.` : ""),
      });
      return;
    }
    setBusy(true);
    try {
      const hecho = await guardarCotizacion(vendedor);
      if (!hecho) return;
      avisos.mostrar({
        tipo: "exito",
        titulo: hecho.saved.nuevaRevision
          ? `Nueva revisión ${hecho.saved.numero} creada`
          : `Cotización guardada · ${hecho.saved.numero}`,
        mensaje: hecho.saved.nuevaRevision
          ? "La versión entregada queda intacta. Ya está en Mis cotizaciones."
          : "Ya está actualizada en Mis cotizaciones.",
      });
    } catch (e) {
      const aviso = explicarError(e);
      avisos.mostrar({ tipo: "error", ...aviso });
    } finally {
      setBusy(false);
    }
  }
  // Clic en una pieza de la vista PDF: sus tarjetas viven en el paso 2.
  useEffect(() => {
    if (irAPieza) startTransition(() => setPaso(2));
  }, [irAPieza]);
  function cambiarPaso(next: number) {
    if (next >= 3 && paso < 3) {
      const errores = erroresAntesDelPaso3();
      if (errores.length) {
        // Desde aquí las piezas incompletas se ven en rojo con lo que falta.
        useEditor.getState().pedirRevision();
        avisos.mostrar({
          tipo: "error",
          titulo: "Antes de continuar, corrige esto",
          mensaje:
            errores.slice(0, 5).join(" · ") +
            (errores.length > 5 ? ` · y ${errores.length - 5} más.` : ""),
        });
        startTransition(() => setPaso(2));
        // El paso 2 se vuelve a montar y abre directo en el primer problema
        // (sitio, pantalla y pieza en rojo: ver Paso2.tsx).
        setIntentosPaso3((n) => n + 1);
        return;
      }
    }
    startTransition(() => setPaso(next));
  }
  function abrirCuenta(s: SeccionCuenta) {
    setAdmin(false);
    setCuenta(s);
  }
  async function salir() {
    if (await reemplazar()) {
      localStorage.removeItem(SESSION_KEY);
      borrarSesion();
      client.removeQueries({ queryKey: ["historial"] });
      useEditor.getState().cargar(nuevaCotizacion());
      setCuenta(null);
      setVendedor(null);
    }
  }
  // Único punto donde se decide el acceso al panel en el cliente. El backend
  // vuelve a exigir rol de admin en cada endpoint (ver routes/vendedores.js),
  // así que esto es comodidad de UI, no el control de acceso real.
  const esAdmin = vendedor?.rol === "admin";
  if (!vendedor)
    return (
      <Login
        onLogin={(v) => {
          // Entra otro vendedor en el mismo equipo tras una sesión vencida:
          // no hereda la cotización ni el historial del anterior.
          if (duenoBorrador.current && duenoBorrador.current !== v.id) {
            useEditor.getState().cargar(nuevaCotizacion());
            client.removeQueries({ queryKey: ["historial"] });
            setPaso(1);
          }
          duenoBorrador.current = null;
          setVendedor(v);
          if (v.rol !== "admin") setAdmin(false);
        }}
      />
    );
  return (
    <>
      <header
        className="app-header"
        ref={cabecera}
        data-menu={menuMovil || undefined}
      >
        <div className="brand">
          <img
            className="logo logo-dark"
            src="/assets/logo-diez-visual-oscuro.svg"
            alt="Diez Visual"
          />
          <img
            className="logo logo-light"
            src="/assets/logo-diez-visual.svg"
            alt="Diez Visual"
          />
          <div>
            <h1>Cotizador Comercial</h1>
            <small>
              {vendedor.nombre} · {numeroOficial ?? "Borrador sin número"}
            </small>
          </div>
        </div>
        <nav className="row header-nav" aria-label="Cuenta">
          <button
            type="button"
            className="menu-movil icon-button ghost"
            aria-label="Menú"
            aria-expanded={menuMovil}
            aria-controls="acciones-cabecera"
            onClick={() => setMenuMovil(!menuMovil)}
          >
            <Icono nombre={menuMovil ? "cerrar" : "menu"} />
          </button>
          <div
            className="row header-acciones"
            id="acciones-cabecera"
            // Elegir una acción cierra el menú del celular.
            onClickCapture={() => setMenuMovil(false)}
          >
            <button
              disabled={busy}
              onClick={async () => {
                if (await reemplazar()) {
                  useEditor.getState().cargar(nuevaCotizacion());
                  setPaso(1);
                  setAdmin(false);
                }
              }}
            >
              <Icono nombre="agregar" /> Nueva cotización
            </button>
            {!admin ? (
              <button
                disabled={busy}
                onClick={() => void guardarDesdeBarra()}
                title="Guarda la cotización y actualiza Mis cotizaciones"
              >
                <Icono nombre="guardar" /> Guardar
              </button>
            ) : null}
            <button disabled={busy} onClick={() => setModal("historial")}>
              <Icono nombre="historial" /> Mis cotizaciones
            </button>
            {esAdmin ? (
              <button
                disabled={busy}
                aria-pressed={admin}
                onClick={() => {
                  setCuenta(null);
                  setAdmin(!admin);
                }}
              >
                <Icono nombre={admin ? "anterior" : "ajustes"} />
                {admin ? "Volver al cotizador" : "Administración"}
              </button>
            ) : null}
          </div>
          <div className="row header-utilidades">
            <button
              className="icon-button ghost"
              onClick={() => setTema(tema === "claro" ? "oscuro" : "claro")}
              aria-label="Cambiar tema"
              title={tema === "claro" ? "Usar tema oscuro" : "Usar tema claro"}
            >
              <Icono key={tema} nombre={tema === "claro" ? "luna" : "sol"} />
            </button>
            <MenuCuenta
              nombre={vendedor.nombre}
              busy={busy}
              onPerfil={() => abrirCuenta("perfil")}
              onPin={() => abrirCuenta("seguridad")}
              onSalir={() => void salir()}
            />
          </div>
        </nav>
      </header>
      <Suspense fallback={<p role="status">Cargando sección…</p>}>
        {cuenta ? (
          <MiCuenta
            vendedor={vendedor}
            seccion={cuenta}
            onSeccion={setCuenta}
            onVolver={() => setCuenta(null)}
            tema={tema}
            onTema={setTema}
            onSalir={() => void salir()}
          />
        ) : admin && esAdmin ? (
          <Admin vendedor={vendedor} />
        ) : (
          <main className="workspace">
            {catalogo.isPending ? (
              <p className="notice" role="status">
                Cargando catálogo. Puedes completar los datos del cliente
                mientras tanto.
              </p>
            ) : null}
            <ErrorAviso
              error={catalogo.error}
              titulo="No se pudo cargar el catálogo"
            />
            {catalogo.isError ? (
              <p className="notice row between">
                Sin catálogo no se pueden agregar piezas.
                <button onClick={() => void catalogo.refetch()}>
                  <Icono nombre="historial" /> Reintentar catálogo
                </button>
              </p>
            ) : null}
            <fieldset className="editor-fieldset" disabled={busy}>
              {/* Opción B: los pasos van en una columna a la izquierda; en el
                  paso 2 sus sub-pasos (Sitio, Detalles…) cuelgan de él. */}
              <div className="editor-layout editor-layout-lateral">
                <nav
                  className="stepper stepper-lateral"
                  aria-label="Pasos del cotizador"
                >
                  {["Cliente", "Proyecto y piezas", "Margen y envío"].map(
                    (nombre, i) => [
                      <button
                        key={nombre}
                        disabled={busy}
                        aria-current={paso === i + 1 ? "step" : undefined}
                        data-hecho={paso > i + 1 || undefined}
                        onClick={() => cambiarPaso(i + 1)}
                      >
                        <span>
                          {paso > i + 1 ? (
                            <>
                              <Icono nombre="check" />
                              <span className="sr-only">{i + 1}</span>
                            </>
                          ) : (
                            i + 1
                          )}
                        </span>
                        {nombre}
                      </button>,
                      i === 1 && paso === 2 ? (
                        <div
                          key="sub"
                          id="p2-subpasos"
                          className="stepper-sub"
                        />
                      ) : null,
                    ],
                  )}
                </nav>
                <div className="editor-main">
                  {paso > 1 ? <Propuestas /> : null}
                  <div
                    className="step-panel"
                    key={`${paso}-${numero}-${paso === 2 ? intentosPaso3 : 0}`}
                  >
                    {paso === 1 ? (
                      <Cliente />
                    ) : paso === 2 ? (
                      // Paso 2 por pantallas: trae su propia barra con
                      // «← Atrás» y «Siguiente →» (y el simulador y la
                      // sugerencia dentro de «Piezas»).
                      <Paso2
                        vendedor={vendedor}
                        onVolver={() => cambiarPaso(1)}
                        onContinuar={() => cambiarPaso(3)}
                        onSugerir={() => setModal("sugerencia")}
                      />
                    ) : (
                      <Margen vendedor={vendedor} />
                    )}
                  </div>
                  {/* El paso 2 trae su propia barra (Paso2.tsx). */}
                  {paso !== 2 ? (
                    <footer className="row between pasos-pie">
                      <button
                        disabled={paso === 1 || busy}
                        onClick={() => cambiarPaso(paso - 1)}
                      >
                        <Icono nombre="anterior" /> Atrás
                      </button>
                      {paso < 3 ? (
                        <button
                          className="primary"
                          onClick={() => cambiarPaso(paso + 1)}
                        >
                          Continuar <Icono nombre="siguiente" />
                        </button>
                      ) : null}
                    </footer>
                  ) : null}
                </div>
                <Resumen vendedor={vendedor} />
              </div>
            </fieldset>
            {paso === 3 ? (
              <fieldset className="editor-fieldset" disabled={busy}>
                <AccionesCotizacion vendedor={vendedor} setBusy={setBusy} />
                <OrdenProduccion vendedor={vendedor} />
              </fieldset>
            ) : null}
          </main>
        )}
        {modal === "historial" ? (
          <Historial
            vendedor={vendedor}
            onClose={() => setModal(null)}
            onLoad={() => {
              setPaso(1);
              setAdmin(false);
            }}
          />
        ) : null}
        {modal === "sugerencia" ? (
          <Sugerencia vendedor={vendedor} onClose={() => setModal(null)} />
        ) : null}
      </Suspense>
    </>
  );
}
